import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { TrashIcon } from '@/icons/trash-icon';
import {
  DiscountCategoryDetail,
  ManageDiscountCategoryDetailStatusPayload,
} from '@/services/discount-management/discount-slabs.types';
import ManageDiscountCategorySlabFormContainer from '../../category/ManageDiscountCategorySlabFormContainer';

type getColumnsInterface = {
  t: (key: string) => string;
  openConfirmation: (data: {
    title: string;
    description: string;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'destructive' | 'warning';
    onAction: () => void | Promise<void>;
    onCancel: () => void;
  }) => void;
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
    onCancel?: () => void;
  }) => void;
  mutateAsync: (payload: ManageDiscountCategoryDetailStatusPayload) => Promise<{ message: string }>;
  categoryId: string;
};

export function getColumns({
  t,
  openConfirmation,
  onOpen,
  mutateAsync,
  categoryId,
}: getColumnsInterface): ColumnDef<DiscountCategoryDetail>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'fromAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.fromAmountDiscount')} />
      ),
      cell: ({ row }) => <div>{row.getValue('fromAmount')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'toAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.toAmountDiscount')} />
      ),
      cell: ({ row }) => <div>{row.getValue('toAmount')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'discountType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.discountType')} />
      ),
      cell: ({ row }) => <div>{row.getValue('discountType')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'value',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.value')} />,
      cell: ({ row }) => <div>{row.getValue('value')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'minValue',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.minValue')} />,
      cell: ({ row }) => <div>{row.getValue('minValue')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'maxValue',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.maxValue')} />,
      cell: ({ row }) => <div>{row.getValue('maxValue')}</div>,
      enableHiding: false,
    },
    // {
    //   accessorKey: 'status',
    //   header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
    //   cell: ({ row }) => {
    //     const status = row.getValue('status');
    //     return (
    //       <div>
    //         {status === 'A' ? (
    //           <span className="text-success px-2 py-0.5 bg-success-subtle rounded-2xl">
    //             <Translation id={'common.page.table.active'} />
    //           </span>
    //         ) : (
    //           <span className="text-destructive-text px-2 py-0.5 bg-destructive-soft/20 rounded-2xl">
    //             <Translation id={'common.page.table.blocked'} />
    //           </span>
    //         )}
    //       </div>
    //     );
    //   },
    //   enableHiding: false,
    // },
    {
      id: 'action',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableHiding: false,
      enableSorting: false,
      accessorKey: 'action',
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.discount.category.detailManage] }}>
            {row.original.status === 'A' && (
              <EditIcon
                className="text-foreground-subtle size-4 hover:cursor-pointer"
                onClick={() => {
                  onOpen({
                    title: 'Add New Discount Slab',
                    okText: <CrumbTitle id="common.page.modal.submit" />,
                    size: 'lg',
                    modalTitle: 'configurationManagement.commissions.manageSlabsModal.title',
                    component: (
                      <ManageDiscountCategorySlabFormContainer
                        discountCategoryId={categoryId}
                        discountCategoryDetailId={String(row.original.discountCategoryDetailId)}
                        defaultValues={{
                          fromAmount: row.getValue('fromAmount'),
                          toAmount: row.getValue('toAmount'),
                          discountType: String(row.getValue('discountType')),
                          value: row.getValue('value'),
                          minValue: row.getValue('minValue'),
                          maxValue: row.getValue('maxValue'),
                        }}
                      />
                    ),

                    onCancel: () => {},
                  });
                }}
              />
            )}
          </Can>
          <Can rule={{ allOf: [PERMS.discount.category.detailStatus] }}>
            <Button
              variant={'ghost'}
              className="p-0"
              onClick={() => {
                const isActive = row.original.status === 'A';
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: isActive
                    ? t('common.page.modal.blockDiscountMessage')
                    : t('common.page.modal.unblockDiscountMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  onAction: async () => {
                    await mutateAsync({
                      categoryId: categoryId,
                      discountCategoryDetailId: String(row.original.discountCategoryDetailId),
                      status: row.original.status === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.original.status === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4 " />
              ) : (
                <LockOpenIcon className="text-link size-4 " />
              )}
            </Button>
          </Can>
          <Can rule={{ allOf: [PERMS.discount.category.detailManage] }}>
            {row.original.status === 'A' && (
              <Button
                variant={'ghost'}
                className="p-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('common.page.modal.deleteItemMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    size: 'lg',
                    onAction: async () => {
                      await mutateAsync({
                        discountCategoryDetailId: row.original.discountCategoryDetailId,
                        categoryId: categoryId,
                        status: 'D',
                      });
                    },
                    onCancel: () => {},
                    variant: 'destructive',
                  });
                }}
              >
                <TrashIcon className="text-error-emphasis size-4 " />
              </Button>
            )}
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
